import React, { useEffect, useMemo, useState } from "react";
import styles from "./DashboardTutorialOverlay.module.scss";

export type DashboardTutorialStep = {
  id: string;
  selector: string;
  title: string;
  text: string;
  placement?: "top" | "bottom" | "left" | "right" | "center";
  actionLabel?: string;
  onAction?: () => void;
  beforeShow?: () => void;
  optional?: boolean;
  onlyPractice?: boolean;
};

type RectState = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type DashboardTutorialOverlayProps = {
  open: boolean;
  steps: DashboardTutorialStep[];
  isPracticeVersion?: boolean;
  onClose: () => void;
};

const TOOLTIP_WIDTH = 390;
const TOOLTIP_HEIGHT = 230;
const GAP = 14;

const DashboardTutorialOverlay: React.FC<DashboardTutorialOverlayProps> = ({
  open,
  steps,
  isPracticeVersion = false,
  onClose,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<RectState | null>(null);
  const [missingTarget, setMissingTarget] = useState(false);

  const visibleSteps = useMemo(() => {
    return steps.filter((step) => {
      if (step.onlyPractice && !isPracticeVersion) return false;
      return true;
    });
  }, [steps, isPracticeVersion]);

  const activeStep = visibleSteps[activeIndex];

  const updateTarget = () => {
    if (!activeStep) return;

    activeStep.beforeShow?.();

    window.setTimeout(() => {
      const target = document.querySelector(activeStep.selector) as HTMLElement | null;

      if (!target) {
        setTargetRect(null);
        setMissingTarget(true);
        return;
      }

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });

      window.setTimeout(() => {
        const rect = target.getBoundingClientRect();

        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });

        setMissingTarget(false);
      }, 260);
    }, 80);
  };

  useEffect(() => {
    if (!open || !activeStep) return;

    updateTarget();

    const handleUpdate = () => updateTarget();

    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate, true);

    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex, activeStep?.selector]);

  useEffect(() => {
    if (!open) {
      setActiveIndex(0);
      setTargetRect(null);
      setMissingTarget(false);
    }
  }, [open]);

  if (!open || !activeStep) return null;

  const isLast = activeIndex === visibleSteps.length - 1;

  const handleNext = () => {
    if (isLast) {
      onClose();
      return;
    }

    setActiveIndex((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveIndex((prev) => Math.max(0, prev - 1));
  };

  const getTooltipPosition = (): React.CSSProperties => {
    if (!targetRect || missingTarget || activeStep.placement === "center") {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const placement = activeStep.placement || "bottom";

    let top = targetRect.top + targetRect.height + GAP;
    let left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2;

    if (placement === "top") {
      top = targetRect.top - TOOLTIP_HEIGHT - GAP;
    }

    if (placement === "left") {
      top = targetRect.top + targetRect.height / 2 - TOOLTIP_HEIGHT / 2;
      left = targetRect.left - TOOLTIP_WIDTH - GAP;
    }

    if (placement === "right") {
      top = targetRect.top + targetRect.height / 2 - TOOLTIP_HEIGHT / 2;
      left = targetRect.left + targetRect.width + GAP;
    }

    const safeLeft = Math.min(
      Math.max(16, left),
      window.innerWidth - TOOLTIP_WIDTH - 16
    );

    const safeTop = Math.min(
      Math.max(16, top),
      window.innerHeight - TOOLTIP_HEIGHT - 16
    );

    return {
      top: safeTop,
      left: safeLeft,
    };
  };

  const cutoutStyle =
    targetRect && !missingTarget
      ? ({
          "--tour-left": `${Math.max(0, targetRect.left - 8)}px`,
          "--tour-top": `${Math.max(0, targetRect.top - 8)}px`,
          "--tour-width": `${targetRect.width + 16}px`,
          "--tour-height": `${targetRect.height + 16}px`,
        } as React.CSSProperties)
      : undefined;

  return (
    <div className={styles.tourRoot}>
      <div
        className={`${styles.backdrop} ${
          targetRect && !missingTarget ? styles.backdropWithCutout : ""
        }`}
        style={cutoutStyle}
      />

      {targetRect && !missingTarget && (
        <div
          className={styles.spotlight}
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
          }}
        />
      )}

      <div className={styles.tooltip} style={getTooltipPosition()}>
        <div className={styles.tooltipHeader}>
          <div>
            <span className={styles.stepText}>
              Step {activeIndex + 1} of {visibleSteps.length}
            </span>
            <h3>{activeStep.title}</h3>
          </div>

          <button type="button" className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        <p>{activeStep.text}</p>

        {missingTarget && (
          <div className={styles.missingTarget}>
            This part is not visible on the current screen. Continue the tutorial when
            you reach that page or section.
          </div>
        )}

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={handleBack}
            disabled={activeIndex === 0}
          >
            Back
          </button>

          <div className={styles.footerRight}>
            {activeStep.actionLabel && activeStep.onAction && (
              <button
                type="button"
                className={styles.darkBtn}
                onClick={activeStep.onAction}
              >
                {activeStep.actionLabel}
              </button>
            )}

            <button type="button" className={styles.primaryBtn} onClick={handleNext}>
              {isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTutorialOverlay;
